import type { AttachmentUpload } from '@live-stream/shared'
import { createMixer, type Mixer } from '../recording/mixer.js'
import { PartUploader, type UploaderProgress } from '../recording/uploader.js'
import type { BubbleSpec } from './bubble.js'
import { trackProcessor } from './insertable-streams.js'
import { paintCapture, planRepaint, shouldPassThrough, type PaintSource } from './paint.js'
import { pickCaptureMime, type CaptureMode, type CaptureProfile } from './profile.js'

/**
 * One take, from the browser: the screen with the camera in a bubble (or
 * the camera alone), the microphone and the tab's or system's sound mixed,
 * recorded by a MediaRecorder — H.264 where the browser offers it, which
 * most machines encode in hardware and is what keeps the cursor and the
 * scrolling smooth; VP8 otherwise — and uploaded in parts as it runs
 * through the same PartUploader and crash buffer a meeting recording uses.
 * Nothing is ever held only in memory, and a tab that dies leaves the take
 * on the disk.
 *
 * With no bubble to draw, the source track is recorded as it is: nothing
 * in between, the encoder's own pace. With the bubble, a canvas on the
 * page is painted and the recorder takes the canvas's track — never a
 * worker: a frame painted in a worker has to be handed across to the
 * page's track, and that hand-over cost a hundred milliseconds a frame on
 * a laptop with a GPU. The compositor is driven by the frames themselves
 * (insertable streams, on Chrome and Edge), not by a timer a hidden tab
 * would throttle, and paints only where something changed.
 *
 * An engine is one take. Restart discards it and the page makes another
 * over the same sources; the sources — the tracks — belong to the page.
 */

export interface CaptureSources {
  mode: CaptureMode
  screen: MediaStreamTrack | null
  camera: MediaStreamTrack | null
  mic: MediaStreamTrack | null
  /** The tab's or system's sound, when the picker offered it and the person ticked it. */
  systemAudio: MediaStreamTrack | null
}

export interface CaptureHooks {
  begin(mimeType: string): Promise<{
    videoId: string
    shareToken: string
    partBytes: number
    label: string
  }>
  address(videoId: string, partNumber: number): Promise<AttachmentUpload>
  complete(
    videoId: string,
    info: { durationMs: number; width: number; height: number },
  ): Promise<void>
  abort(videoId: string): Promise<void>
  onProgress?(progress: UploaderProgress): void
}

export type CaptureState =
  'idle' | 'ready' | 'recording' | 'paused' | 'stopping' | 'done' | 'discarded'

/** Once a second, what the compositor did. */
export interface CompositorStats {
  /** Frames handed to the recorder in the last second; null when the source track is recorded as it is. */
  fps: number | null
  /** Average time one paint took, milliseconds. */
  paintMs: number
  passthrough: boolean
  /** Source frames that arrived before the next frame was due and waited for the one after. */
  coalesced: number
}

const TIMESLICE_MS = 2000
const KEEPALIVE_MS = 1000
const STATS_MS = 1000

interface Compositor {
  track: CanvasCaptureMediaStreamTrack
  /** Something changed that is not a source frame — the bubble moved. */
  nudge(): void
  /** The camera now feeding the bubble: a new track, or none (it was let go). */
  setCamera(track: MediaStreamTrack | null): void
  stop(): void
}

export class CaptureEngine {
  state: CaptureState = 'idle'
  /** What went wrong, if the browser stopped the recorder. */
  error: string | null = null
  /** The MIME type the take is recorded with, once it has started. */
  mimeType: string | null = null
  /** The bubble is painted in, and may move; otherwise the source is recorded as it is for this take. */
  compositing = false
  /** What the compositor reports each second, for the debug line. */
  onStats: ((stats: CompositorStats) => void) | null = null

  private output: MediaStreamTrack | null = null
  private compositor: Compositor | null = null
  private mixer: Mixer | null = null
  private hasAudio = false
  private recorder: MediaRecorder | null = null
  private uploader: PartUploader | null = null
  private videoId: string | null = null
  private shareToken: string | null = null
  private startedAt = 0
  private pausedAt = 0
  private pausedTotal = 0
  private finalMs = 0
  private bubble: BubbleSpec
  private bubbleDirty = false
  private statsTimer: ReturnType<typeof setInterval> | null = null
  private statsSince = 0
  private stats = { written: 0, paints: 0, paintMs: 0, coalesced: 0 }

  constructor(
    private readonly sources: CaptureSources,
    private readonly profile: CaptureProfile,
    bubble: BubbleSpec,
    private readonly hooks: CaptureHooks,
  ) {
    this.bubble = bubble
  }

  /** Everything the browser needs to record at all. */
  static supported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' &&
      pickCaptureMime() !== null &&
      typeof HTMLCanvasElement !== 'undefined' &&
      'captureStream' in HTMLCanvasElement.prototype
    )
  }

  /** Insertable streams: frames as they are captured, so the compositor keeps its pace in a hidden tab. */
  static framesSupported(): boolean {
    return trackProcessor() !== undefined && typeof VideoFrame !== 'undefined'
  }

  /** Decide the path, build the compositor if there is one, and the mix. */
  prepare(): void {
    if (this.output) return
    const { mode, screen, camera } = this.sources
    const primary = mode === 'camera' ? camera : screen
    if (!primary)
      throw new Error(mode === 'camera' ? 'No camera to record.' : 'No screen to record.')
    this.compositing = !shouldPassThrough(mode, this.bubble.visible, camera !== null)
    if (this.compositing) {
      this.compositor = this.startCompositor()
      this.output = this.compositor.track
    } else {
      this.output = primary
    }
    this.mixer = createMixer()
    if (this.mixer) {
      if (this.sources.mic) {
        this.mixer.add('mic', this.sources.mic, 1)
        this.hasAudio = true
      }
      if (this.sources.systemAudio) {
        this.mixer.add('system', this.sources.systemAudio, 1)
        this.hasAudio = true
      }
    }
    this.statsSince = performance.now()
    this.statsTimer = setInterval(() => this.report(), STATS_MS)
    this.state = 'ready'
  }

  /**
   * The canvas the recorder records, painted from the sources as their
   * frames arrive. A frame is painted when one is due (at most the profile's
   * rate); one that arrives sooner waits, and the newest wins. A still
   * screen gets a keepalive frame a second so time still passes.
   */
  private startCompositor(): Compositor {
    const canvas = document.createElement('canvas')
    canvas.width = this.profile.width
    canvas.height = this.profile.height
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('canvas 2d unavailable')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    const track = canvas.captureStream(0).getVideoTracks()[0] as
      CanvasCaptureMediaStreamTrack | undefined
    if (!track) throw new Error('canvas captureStream gave no track')

    const mode = this.sources.mode
    const minFrameMs = 1000 / this.profile.frameRate
    const latest: { screen: PaintSource | null; camera: PaintSource | null } = {
      screen: null,
      camera: null,
    }
    const dirty = { screen: false, camera: false }
    let lastWriteAt = 0
    const cleanups: Array<() => void> = []

    const paintNow = (now: number): void => {
      const plan = planRepaint({
        mode,
        screenChanged: dirty.screen,
        cameraChanged: dirty.camera,
        bubbleChanged: this.bubbleDirty,
        bubbleVisible: this.bubble.visible,
        sinceWriteMs: now - lastWriteAt,
        keepaliveMs: KEEPALIVE_MS,
      })
      if (plan === 'skip') return
      if (plan !== 'keepalive') {
        const before = performance.now()
        try {
          paintCapture(
            ctx,
            canvas.width,
            canvas.height,
            { mode, bubble: this.bubble, screen: latest.screen, camera: latest.camera },
            plan,
          )
        } catch {
          // A source frame closed under us; the next one paints.
          return
        }
        this.stats.paints++
        this.stats.paintMs += performance.now() - before
        dirty.screen = false
        dirty.camera = false
        this.bubbleDirty = false
      }
      track.requestFrame()
      lastWriteAt = now
      this.stats.written++
    }

    /** A source frame arrived, or the bubble moved: paint if a frame is due. */
    const onChange = (): void => {
      const now = performance.now()
      if (now - lastWriteAt < minFrameMs * 0.75) {
        this.stats.coalesced++
        return
      }
      paintNow(now)
    }

    let setCameraSource: (track: MediaStreamTrack | null) => void = () => undefined

    if (CaptureEngine.framesSupported()) {
      // Frames as they are captured. The read loop is not a timer, so a
      // hidden tab does not slow it; the newest frame of each source is held
      // until the next replaces it.
      const pump = (source: MediaStreamTrack, which: 'screen' | 'camera'): (() => void) => {
        const Processor = trackProcessor()!
        const reader = new Processor({ track: source }).readable.getReader()
        let held: VideoFrame | null = null
        let stopped = false
        const stop = (): void => {
          if (stopped) return
          stopped = true
          void reader.cancel().catch(() => undefined)
          held?.close()
          held = null
        }
        cleanups.push(stop)
        void (async () => {
          try {
            for (;;) {
              const { value, done } = await reader.read()
              if (done || !value) break
              if (stopped) {
                value.close()
                break
              }
              const previous = held
              held = value
              latest[which] = {
                image: value,
                width: value.displayWidth,
                height: value.displayHeight,
              }
              dirty[which] = true
              previous?.close()
              onChange()
            }
          } catch {
            // The track ended or the reader was cancelled.
          }
        })()
        return stop
      }
      if (this.sources.screen) pump(this.sources.screen, 'screen')
      let stopCamera = this.sources.camera ? pump(this.sources.camera, 'camera') : null
      // The camera was let go, or came back: the old reading stops and a new one starts.
      setCameraSource = (track) => {
        stopCamera?.()
        stopCamera = track ? pump(track, 'camera') : null
      }
    } else {
      // Firefox: <video> elements polled on a timer — a hidden tab throttles
      // it; the best there is without insertable streams.
      const play = (source: MediaStreamTrack | null): HTMLVideoElement | null => {
        if (!source) return null
        const el = document.createElement('video')
        el.muted = true
        el.playsInline = true
        el.autoplay = true
        el.srcObject = new MediaStream([source])
        void el.play().catch(() => undefined)
        return el
      }
      const els = { screen: play(this.sources.screen), camera: play(this.sources.camera) }
      const seen = { screen: -1, camera: -1 }
      setCameraSource = (track) => {
        if (els.camera) els.camera.srcObject = null
        els.camera = play(track)
        seen.camera = -1
      }
      const poll = (): void => {
        for (const which of ['screen', 'camera'] as const) {
          const el = els[which]
          if (!el || el.readyState < 2 || el.videoWidth === 0) continue
          if (el.currentTime === seen[which]) continue
          seen[which] = el.currentTime
          latest[which] = { image: el, width: el.videoWidth, height: el.videoHeight }
          dirty[which] = true
        }
        onChange()
      }
      const timer = setInterval(poll, Math.round(minFrameMs))
      cleanups.push(() => {
        clearInterval(timer)
        for (const el of Object.values(els)) if (el) el.srcObject = null
      })
    }

    const keepalive = setInterval(() => {
      const now = performance.now()
      if (now - lastWriteAt >= KEEPALIVE_MS) paintNow(now)
    }, KEEPALIVE_MS)
    cleanups.push(() => clearInterval(keepalive))
    paintNow(performance.now())

    return {
      track,
      nudge: onChange,
      setCamera: (camera) => {
        setCameraSource(camera)
        // What was painted from the old camera is gone, and the bubble repaints at once.
        latest.camera = null
        dirty.camera = true
        onChange()
      },
      stop: () => {
        for (const cleanup of cleanups) cleanup()
        track.stop()
      },
    }
  }

  private report(): void {
    const now = performance.now()
    const seconds = Math.max(0.001, (now - this.statsSince) / 1000)
    const s = this.stats
    this.onStats?.({
      fps: this.compositing ? Math.round(s.written / seconds) : null,
      paintMs: s.paints > 0 ? Math.round((s.paintMs / s.paints) * 10) / 10 : 0,
      passthrough: !this.compositing,
      coalesced: s.coalesced,
    })
    this.stats = { written: 0, paints: 0, paintMs: 0, coalesced: 0 }
    this.statsSince = now
  }

  /**
   * Hand the take a camera mid-take, or take it away: the page let the camera go while the
   * bubble was hidden, and asks for it again when it is shown. Nothing to do when the take
   * records the screen as it is (there is no bubble to feed).
   */
  setCamera(track: MediaStreamTrack | null): void {
    this.sources.camera = track
    this.compositor?.setCamera(track)
  }

  /** Move the bubble mid-take. Nothing to move when the source is recorded as it is. */
  setBubble(spec: BubbleSpec): void {
    this.bubble = spec
    this.bubbleDirty = true
    this.compositor?.nudge()
  }

  async start(): Promise<void> {
    if (!this.output) this.prepare()
    if (this.state !== 'ready' || !this.output) throw new Error('The take has already started.')
    const mimeType = pickCaptureMime()
    if (!mimeType) throw new Error('This browser cannot record video.')
    this.mimeType = mimeType
    const tracks: MediaStreamTrack[] = [this.output]
    if (this.mixer && this.hasAudio) tracks.push(this.mixer.track)

    const begun = await this.hooks.begin(mimeType)
    this.videoId = begun.videoId
    this.shareToken = begun.shareToken
    this.uploader = new PartUploader(
      {
        fileId: begun.videoId,
        recordingId: begun.videoId,
        roomSlug: '',
        kind: 'capture',
        mimeType,
        label: begun.label,
        startedAt: Date.now(),
        durationMs: null,
        finished: false,
      },
      begun.partBytes,
      {
        addressFor: (n) => this.hooks.address(begun.videoId, n),
        complete: (durationMs) =>
          this.hooks.complete(begun.videoId, {
            durationMs: durationMs ?? this.elapsedMs,
            width: this.profile.width,
            height: this.profile.height,
          }),
        onProgress: (p) => this.hooks.onProgress?.(p),
      },
    )
    const recorder = new MediaRecorder(new MediaStream(tracks), {
      mimeType,
      videoBitsPerSecond: this.profile.videoBitsPerSecond,
      audioBitsPerSecond: this.profile.audioBitsPerSecond,
    })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.uploader?.push(e.data)
    }
    recorder.onerror = () => {
      this.error ??= 'The browser stopped the recorder.'
    }
    recorder.start(TIMESLICE_MS)
    this.recorder = recorder
    this.startedAt = Date.now()
    this.pausedTotal = 0
    this.state = 'recording'
  }

  pause(): void {
    if (this.state !== 'recording' || !this.recorder) return
    this.recorder.pause()
    this.pausedAt = Date.now()
    this.state = 'paused'
  }

  resume(): void {
    if (this.state !== 'paused' || !this.recorder) return
    this.recorder.resume()
    this.pausedTotal += Date.now() - this.pausedAt
    this.state = 'recording'
  }

  /** Recorded time so far, pauses excluded. */
  get elapsedMs(): number {
    switch (this.state) {
      case 'recording':
        return Date.now() - this.startedAt - this.pausedTotal
      case 'paused':
        return this.pausedAt - this.startedAt - this.pausedTotal
      default:
        return this.finalMs
    }
  }

  /** Stop, send the last parts, complete. Resolves when storage has it all. */
  async stop(): Promise<{ videoId: string; shareToken: string }> {
    if (this.state !== 'recording' && this.state !== 'paused') {
      throw new Error('Nothing is being recorded.')
    }
    this.finalMs = this.elapsedMs
    this.state = 'stopping'
    await this.stopRecorder()
    this.teardown()
    try {
      await this.uploader?.stop(this.finalMs)
    } finally {
      this.state = 'done'
    }
    if (this.error) throw new Error(this.error)
    return { videoId: this.videoId!, shareToken: this.shareToken! }
  }

  /** Throw the take away: the recorder stopped, the parts forgotten, the server told. */
  async discard(): Promise<void> {
    const wasRunning = this.state === 'recording' || this.state === 'paused'
    this.finalMs = this.elapsedMs
    this.state = 'discarded'
    if (wasRunning) await this.stopRecorder()
    this.teardown()
    await this.uploader?.abandon()
    this.uploader = null
    if (this.videoId) await this.hooks.abort(this.videoId).catch(() => undefined)
  }

  /** Let go of the compositor and the mix. The source tracks are not ours to stop. */
  dispose(): void {
    this.teardown()
    this.mixer?.stop()
    this.mixer = null
    if (this.state === 'idle' || this.state === 'ready') this.state = 'discarded'
  }

  private stopRecorder(): Promise<void> {
    const recorder = this.recorder
    this.recorder = null
    if (!recorder || recorder.state === 'inactive') return Promise.resolve()
    return new Promise((resolve) => {
      recorder.addEventListener('stop', () => resolve(), { once: true })
      try {
        recorder.stop()
      } catch {
        resolve()
      }
    })
  }

  private teardown(): void {
    if (this.statsTimer) clearInterval(this.statsTimer)
    this.statsTimer = null
    this.compositor?.stop()
    this.compositor = null
    // A pass-through output is the source's own track: the page stops it.
    this.output = null
  }
}
